/**
 * Customer-facing subscription management screen.
 *
 * Displays the authenticated customer's current plan name, billing
 * status, expiry date, and jobs limit — fetched via the unified API
 * client's `useSubscriptionMe()` hook (`@/lib/api/apiClient`) from
 * `GET /api/subscriptions/me` (`functions/api/subscriptions/me.ts`).
 *
 * ------------------------------------------------------------------
 * Migrated for "Unified API client with RTK Query for all frontend data
 * fetching": the previous version of this component never called a real
 * backend endpoint — it rendered hard-coded placeholder subscription
 * data (`tier`/`siloMode`/`stripeSubscriptionId` fields with no matching
 * live endpoint anywhere in this codebase) behind `// TODO: Replace with
 * real API call` comments, and a `handleUpgradeTier` action that only
 * mutated local component state. The only real, live, request-routable
 * subscription endpoint for the *authenticated customer* (as opposed to
 * the admin console) is `GET /api/subscriptions/me` — already wired up
 * end-to-end (TanStack Query + Zod + Supabase-auth-token injection) by
 * the root `src/` tree's `src/app/routes/Subscription.tsx` (GH issue
 * #170), which this `apps/web`-native version follows the same shape as,
 * now going through the shared `@/lib/api/apiClient` hook instead of a
 * component-local `fetchSubscriptionMe`.
 *
 * The tier-upgrade / Enterprise-silo-mode comparison UI the placeholder
 * version rendered has no backing endpoint yet... UPDATE (GitHub issue
 * "[Billing] Checkout Sessions + stored keys — kill the email-heuristic
 * linkage", synced from app-iii-partners/hero-hire-ai#274, this repo's
 * #603): a real "Upgrade" action now exists — `useCreateCheckoutSession()`
 * (`@/lib/api/apiClient`), which hits the LIVE `POST
 * /api/stripe/checkout-session` endpoint (`functions/api/stripe/checkout-session.ts`'s
 * `onRequestPost`, GitHub issue #174) and redirects the whole browser to
 * the returned Stripe-hosted Checkout `url` (`checkout.stripe.com`) — no
 * `@stripe/stripe-js`/publishable key needed for a plain redirect.
 *
 * A customer with NO subscription row gets a `404` from
 * `GET /api/subscriptions/me` (see that endpoint's doc-comment); this
 * screen treats that specific status as "No active plan" (offering the
 * Upgrade button), distinct from any OTHER fetch failure (network error,
 * 401, 500, ...) which still renders the generic error/retry UI below —
 * "no subscription yet" is an expected, common state, not an error.
 *
 * `data.planTier` ('basic' | 'pro' | 'enterprise', GitHub issue #169,
 * added to `SubscriptionMeDTO` by this same ticket) drives the
 * "Plan: {Tier} — {Status}" heading — a generic tier label independent of
 * whatever the underlying `subscription_plans.name` marketing name is
 * (e.g. "Hero Monthly"), so any 'pro'-tier plan a customer completes
 * Checkout for renders "Plan: Pro — Active" once their subscription goes
 * active.
 *
 * Related: GitHub issue #170, #174, #603, docs/v2/04-stripe-billing.md §3.2/§3.3/§4
 */

import { useEffect } from 'react';

import { useSubscriptionMe, useCreateCheckoutSession, reportApiError } from '@/lib/api/apiClient';
import { ApiError } from '@/lib/api/fetchClient';
import { Skeleton } from '@/components/ui/skeleton';
import type { AppSubscriptionStatus, SubscriptionPlanTier } from '@/lib/validation/subscriptions';

const STATUS_LABELS: Record<AppSubscriptionStatus, string> = {
  TRIALING: 'Trialing',
  ACTIVE: 'Active',
  PAST_DUE: 'Past Due',
  CANCELED: 'Cancelled',
  UNPAID: 'Unpaid',
};

/** Generic, marketing-name-independent tier label — distinct from
 * `planName` (e.g. "Hero Monthly") — used in the "Plan: {Tier} —
 * {Status}" heading. */
const TIER_LABELS: Record<SubscriptionPlanTier, string> = {
  basic: 'Basic',
  pro: 'Pro',
  enterprise: 'Enterprise',
};

/** Statuses under which a subscription is expected to keep renewing
 * (vs. `CANCELED`/`UNPAID`, where any date shown is a hard expiry). */
const RENEWING_STATUSES: ReadonlySet<AppSubscriptionStatus> = new Set([
  'TRIALING',
  'ACTIVE',
  'PAST_DUE',
]);

const STATUS_BADGE_CLASSNAMES: Record<AppSubscriptionStatus, string> = {
  TRIALING: 'bg-blue-100 text-blue-800',
  ACTIVE: 'bg-green-100 text-green-800',
  PAST_DUE: 'bg-yellow-100 text-yellow-800',
  CANCELED: 'bg-gray-100 text-gray-800',
  UNPAID: 'bg-red-100 text-red-800',
};

/** Edge case: a `null` `expiresAt` renders "No expiry" instead of an
 * `Invalid Date` string. */
function formatExpiresAt(expiresAt: string | null): string {
  if (!expiresAt) return 'No expiry';
  const parsed = new Date(expiresAt);
  if (Number.isNaN(parsed.getTime())) return 'No expiry';
  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * GitHub issue #242/#559: a `null` jobs limit is the explicit "unlimited"
 * sentinel (e.g. Sean Gassett's quarterly plan) and must render
 * "Unlimited" — NOT be treated as "No active jobs". That exact confusion
 * (a paying-unlimited customer's limit reading as exhausted) is the bug
 * this issue fixes. A `0` jobs limit (a genuinely-capped, exhausted plan)
 * still renders "No active jobs".
 */
function formatJobsLimit(currentJobsLimit: number | null): string {
  if (currentJobsLimit === null) return 'Unlimited';
  return currentJobsLimit > 0 ? String(currentJobsLimit) : 'No active jobs';
}

/** The Stripe Price ID the "Upgrade" button subscribes the customer to.
 * Read at call time (not module scope) — see AGENT rules on env-safety;
 * an unset value degrades to a disabled button rather than a crash. */
function getUpgradePriceId(): string | undefined {
  return import.meta.env.VITE_STRIPE_PRO_PRICE_ID || undefined;
}

export const Subscription: React.FC = () => {
  const { data, isLoading, isError, error, refetch, isFetching } = useSubscriptionMe();
  const createCheckoutSession = useCreateCheckoutSession();

  const hasNoActivePlan = isError && error instanceof ApiError && error.status === 404;

  // Requirement #8: sonner error toast, fired once per failed fetch (not
  // once per re-render) — but NOT for the expected "no subscription yet"
  // 404, which renders its own dedicated "No active plan" state below
  // rather than an error toast.
  useEffect(() => {
    if (isError && !hasNoActivePlan) {
      reportApiError(error, 'Failed to load your subscription. Please try again.');
    }
  }, [isError, hasNoActivePlan, error]);

  const upgradePriceId = getUpgradePriceId();

  const handleUpgrade = () => {
    if (!upgradePriceId) {
      reportApiError(
        new Error('Upgrade is not configured (missing VITE_STRIPE_PRO_PRICE_ID)'),
        'Upgrade is temporarily unavailable. Please try again later.'
      );
      return;
    }
    createCheckoutSession.mutate(
      { planId: upgradePriceId },
      {
        onSuccess: (session) => {
          if (session.url) {
            // Redirect the whole browser to the Stripe-hosted Checkout
            // page (https://checkout.stripe.com/...) — demo step 1.
            window.location.href = session.url;
          } else {
            reportApiError(
              new Error('Checkout session created with no redirect URL'),
              'Could not start checkout. Please try again.'
            );
          }
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6 space-y-3" aria-busy="true">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/4" />
      </div>
    );
  }

  // "No active plan" — the caller has no `subscriptions` row (404), e.g.
  // a brand-new customer, or one who cancelled at Stripe Checkout before
  // completing payment (this ticket's negative test case: no
  // subscription row is ever created/linked on a cancelled Checkout, so
  // this remains the state they land back on).
  if (hasNoActivePlan) {
    return (
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold mb-6">Subscription</h2>
        <div className="border border-gray-200 rounded-lg p-6 text-center space-y-4">
          <div className="text-lg font-semibold text-gray-700">No active plan</div>
          <div className="text-sm text-gray-500">
            Upgrade to a paid plan to start posting jobs and sourcing candidates.
          </div>
          <button
            type="button"
            onClick={handleUpgrade}
            disabled={createCheckoutSession.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
          >
            {createCheckoutSession.isPending ? 'Redirecting…' : 'Upgrade'}
          </button>
        </div>
      </section>
    );
  }

  if (isError || !data) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
        <div className="text-red-800 font-semibold">Error</div>
        <div className="text-red-700">We could not load your subscription details.</div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="px-3 py-2 border border-red-300 rounded-md hover:bg-red-100 text-sm font-medium disabled:opacity-50"
        >
          Retry
        </button>
      </div>
    );
  }

  const renewalLabel = RENEWING_STATUSES.has(data.status) ? 'Next renewal' : 'Expires';

  return (
    <section className="bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-bold mb-2">Subscription</h2>
      <p className="text-lg font-semibold text-gray-800 mb-6">
        {`Plan: ${TIER_LABELS[data.planTier]} — ${STATUS_LABELS[data.status]}`}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-2">Plan</div>
          <div className="text-2xl font-bold mb-4">{data.planName}</div>
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-2">Status</div>
          <span
            className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${STATUS_BADGE_CLASSNAMES[data.status]}`}
          >
            {STATUS_LABELS[data.status]}
          </span>
        </div>
      </div>

      <div className="border-t pt-6">
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <dt className="text-sm text-gray-600">{renewalLabel}</dt>
            <dd className="font-medium">{formatExpiresAt(data.expiresAt)}</dd>
          </div>
          <div>
            <dt className="text-sm text-gray-600">Jobs limit</dt>
            <dd className="font-medium">{formatJobsLimit(data.currentJobsLimit)}</dd>
          </div>
        </dl>
      </div>

      <button
        type="button"
        onClick={() => refetch()}
        disabled={isFetching}
        className="mt-6 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm font-medium disabled:opacity-50"
      >
        Refresh
      </button>
    </section>
  );
};

export default Subscription;
